import { Toast } from '@base-ui/react/toast';
import { cn } from '@/shared/lib';
import { notificationStyles } from '@/shared/ui/notification/styles';
import { NotificationCard } from '@/shared/ui/notification/parts/card';
import type { NotificationViewportProps } from '@/shared/ui/notification/types';

/**
 * The fixed region notifications stack into.
 *
 * Top-right, over every other layer. The region itself is
 * `pointer-events-none` so the empty space never intercepts a click meant for
 * the page; each card re-enables pointer events for itself.
 *
 * `aria-label` names the region for a screen reader that lands in it. The
 * announcements themselves come from Base UI's live region, not from this
 * element — a viewport is a container, not an alert.
 */
export function NotificationViewport({ className }: NotificationViewportProps) {
  return (
    <Toast.Portal>
      <Toast.Viewport
        className={cn(notificationStyles.viewport, className)}
        aria-label="Notifications"
      >
        <ToastList />
      </Toast.Viewport>
    </Toast.Portal>
  );
}

/**
 * The list of live notifications.
 *
 * Split from the viewport so the viewport stays a pure container and this stays
 * the one place that reads the manager's list. Base UI renders each toast
 * through `Toast.Root`, which supplies the swipe-to-dismiss and transition
 * behaviour; the card supplies the appearance.
 */
function ToastList() {
  const { toasts } = Toast.useToastManager();

  return (
    <>
      {toasts.map((toast) => (
        <Toast.Root key={toast.id} toast={toast} className={notificationStyles.root({ tone: toneOf(toast.type) })}>
          <NotificationCard toast={toast} />
        </Toast.Root>
      ))}
    </>
  );
}

/**
 * Narrow Base UI's free-form `type` string back to our tone union.
 *
 * Base UI types `type` as `string | undefined` because it is generic over the
 * consumer's own vocabulary. We only ever write the four tones, so anything
 * else is treated as `info` rather than crashing a render.
 */
function toneOf(type: string | undefined): 'success' | 'error' | 'warning' | 'info' {
  if (type === 'success' || type === 'error' || type === 'warning' || type === 'info') {
    return type;
  }
  return 'info';
}
